import { useCallback, useEffect, useMemo, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import { NetworkStatus } from '@apollo/client'

import { useTeamStaleGroupIncidentsQuery } from '@/graphql/generated/hooks'
import { IncidentStatus } from '@/graphql/generated/schemas'
import { trackEvent } from '@/utils/analytics'

import {
  TEAM_STALE_DETAILS_PAGE_SIZE,
  TEAM_STALE_UNASSIGNED_GROUP_ID,
  TEAM_STALE_WORK_ANALYTICS,
} from '../constants/constants'
import type { TeamStaleIncident } from '../types/types'
import { getTeamAckTimestamp } from '../utils/utils'

interface UseTeamStaleGroupIncidentsIProps {
  /** The open snapshot's staleness cutoff, shared with the grouping query. */
  cutoff: string
  isExpanded: boolean
  /** The group's attribution key; null is the Unassigned bucket. */
  userId: string | null
}

interface UseTeamStaleGroupIncidentsResultIProps {
  incidents: TeamStaleIncident[]
  /** First page (or retry) in flight. */
  isLoading: boolean
  /** A subsequent page in flight. */
  isLoadingMore: boolean
  hasMore: boolean
  /**
   * The first page has landed. Distinguishes a genuinely emptied group from
   * one whose query hasn't run (a collapsed section also has zero incidents
   * and isLoading false, because the query is skipped).
   */
  hasLoadedFirstPage: boolean
  /**
   * Loaded ids the drawer has since resolved (dropped from `incidents` at
   * render time). The section prunes these from the modal's selection so
   * the ActionBar never counts rows that are no longer shown.
   */
  resolvedLoadedIds: string[]
  /** How many of the group's incidents have no loaded details yet. */
  remainingCount: number
  loadMore: () => void
  isError: boolean
  retry: () => void
}

/**
 * One expanded group's hydrated incident cards, paged server-side via the
 * ENG-32213 attribution filter (`attributedToIdIn` for a user's bucket,
 * `attributedToIsNull` for Unassigned — matching the server's
 * COALESCE(owner_id, guard_id) grouping key): nothing is requested while
 * the section is collapsed; the first expansion fetches page 0 and "show
 * more" appends the next page (`first` = zero-based page index, `last` =
 * TEAM_STALE_DETAILS_PAGE_SIZE). Resolved incidents drop out at render
 * time — the status filter below reads the normalized cache entry, so a
 * drawer resolve removes the card without a refetch. Collapse does not
 * reset anything (re-expanding shows instantly from accumulated data); the
 * modal unmounts its sections on close, so each open starts clean.
 */
export const useTeamStaleGroupIncidents = ({
  cutoff,
  isExpanded,
  userId,
}: UseTeamStaleGroupIncidentsIProps): UseTeamStaleGroupIncidentsResultIProps => {
  const [hasRequested, setHasRequested] = useState(false)
  useEffect(() => {
    if (isExpanded && !hasRequested) setHasRequested(true)
  }, [hasRequested, isExpanded])

  const {
    data,
    loading: isQueryLoading,
    networkStatus,
    error,
    refetch,
    fetchMore,
  } = useTeamStaleGroupIncidentsQuery({
    // Surfaces fetchMore/refetch progress through `networkStatus` instead of
    // a hand-rolled loading flag.
    notifyOnNetworkStatusChange: true,
    skip: !hasRequested,
    variables: {
      actionUpdatedAtLte: cutoff,
      // Exactly one of the two attribution filters per group: a user's
      // bucket by id, or the no-owner-no-guard bucket. The unused variable
      // is omitted and the backend ignores the null filter field.
      ...(userId
        ? { attributedToIdIn: [userId] }
        : { attributedToIsNull: true }),
      first: 0,
      last: TEAM_STALE_DETAILS_PAGE_SIZE,
    },
  })

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags: { action: 'fetch_team_group_details', feature: 'stale_work' },
      })
    }
  }, [error])

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore
  const isLoading = isQueryLoading && !isLoadingMore

  const totalCount = data?.outstandingIncidents?.totalCount ?? 0

  const { incidents, resolvedLoadedIds, uniqueLoadedCount } = useMemo(() => {
    // Deduped by id: a mid-session resolve shifts the CREATED_AT page
    // boundaries, so a later page can re-return an incident an earlier one
    // already delivered — duplicate React keys otherwise.
    const incidentsById = new Map<string, TeamStaleIncident>()
    for (const edge of data?.outstandingIncidents?.edges ?? []) {
      const node = edge?.node
      if (!node) continue
      if (!incidentsById.has(node.id)) incidentsById.set(node.id, node)
    }
    return {
      incidents: [...incidentsById.values()]
        // The drawer resolves through the normalized cache — drop resolved
        // cards at render time, like the previous implementation.
        .filter((node) => node.status !== IncidentStatus.Resolved)
        // Re-sort loaded rows by acknowledgment time: the server pages by
        // CREATED_AT because it cannot sort by ack time (see the .gql
        // comment), so within the loaded set "oldest ack first" is restored
        // client-side.
        .sort((a, b) => getTeamAckTimestamp(a) - getTeamAckTimestamp(b)),
      resolvedLoadedIds: [...incidentsById.values()]
        .filter((node) => node.status === IncidentStatus.Resolved)
        .map((node) => node.id),
      uniqueLoadedCount: incidentsById.size,
    }
  }, [data])
  const activeLoadedCount = incidents.length

  // Progress is judged on unique loaded rows, never raw accumulated edges:
  // raw edges keep counting a card the drawer already resolved, while the
  // server's filtered set no longer does — the mismatch would end paging
  // with an active incident never loaded (fetch 50 of 51, resolve one, page
  // 1 comes back empty and the 51st card is unreachable).
  const remainingCount = Math.max(totalCount - uniqueLoadedCount, 0)
  const hasMore = uniqueLoadedCount > 0 && remainingCount > 0

  const loadMore = useCallback(() => {
    // isLoading blocks a click while page 0 is still in flight: a concurrent
    // fetchMore's updateQuery would merge against edges that haven't settled
    // yet and could drop or duplicate the initial page.
    if (isLoading || isLoadingMore || !hasMore) return
    // The server's filtered list drops resolved incidents, so the first
    // unloaded row sits at index = count of loaded rows still active; floor
    // of the ACTIVE count re-requests the shifted boundary page after a
    // drawer resolve (a retry's refetch resets the derivation with page 0),
    // and the id dedupe above absorbs any overlap.
    const nextPage = Math.floor(
      activeLoadedCount / TEAM_STALE_DETAILS_PAGE_SIZE
    )
    trackEvent(TEAM_STALE_WORK_ANALYTICS.loadMore, {
      page: nextPage,
      per_page: TEAM_STALE_DETAILS_PAGE_SIZE,
      user_id: userId ?? TEAM_STALE_UNASSIGNED_GROUP_ID,
    })
    fetchMore({
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.outstandingIncidents) return previous
        return {
          ...fetchMoreResult,
          outstandingIncidents: {
            ...fetchMoreResult.outstandingIncidents,
            edges: [
              ...(previous.outstandingIncidents?.edges ?? []),
              ...(fetchMoreResult.outstandingIncidents.edges ?? []),
            ],
          },
        }
      },
      variables: { first: nextPage },
    }).catch((pageError) => {
      // fetchMore rejections do NOT flow into the hook's `error`, so
      // report them explicitly; the button stays visible for another try.
      Sentry.captureException(pageError, {
        tags: {
          action: 'fetch_team_group_details_more',
          feature: 'stale_work',
        },
      })
    })
  }, [activeLoadedCount, fetchMore, hasMore, isLoading, isLoadingMore, userId])

  // Apollo's refetch rejects on failure; the rejection is already surfaced
  // through the hook's `error` (and the Sentry effect above), so swallow it
  // to avoid an unhandled promise rejection from the retry button.
  const retry = useCallback(() => {
    refetch().catch(() => undefined)
  }, [refetch])

  return {
    hasLoadedFirstPage: data !== undefined,
    hasMore,
    incidents,
    isError: Boolean(error),
    isLoading,
    isLoadingMore,
    loadMore,
    remainingCount,
    resolvedLoadedIds,
    retry,
  }
}
